import NiceModal from '@ebay/nice-modal-react';
import { ConfirmDialog } from './ConfirmDialog';
import type { ConfirmDialogProps, ConfirmDialogResult } from './ConfirmDialog';

/**
 * Imperative confirmation dialog — callable from anywhere (OOP classes,
 * plain functions, event handlers, outside the React tree).
 *
 * Returns a typed `ConfirmDialogResult` with `{ confirmed, remarks? }`.
 * On cancel/dismiss, `confirmed` is always `false`.
 *
 * ## Simple confirmation (dialog closes immediately on click)
 *
 * ```ts
 * const { confirmed } = await confirm({ title: 'Delete this item?' });
 * if (!confirmed) return;
 * await deleteItem();
 * ```
 *
 * ## With async work (dialog stays open with loading spinner until done)
 *
 * ```ts
 * const { confirmed } = await confirm({
 *   title: 'Send invite?',
 *   onAction: async () => {
 *     await sendInviteApi(participantId);
 *   },
 * });
 * ```
 *
 * For React components, prefer the `useConfirm()` hook instead.
 */
export async function confirm(props: ConfirmDialogProps): Promise<ConfirmDialogResult> {
  const result = await NiceModal.show(ConfirmDialog, props);
  if (result != null && typeof result === 'object' && 'confirmed' in result) {
    return result as ConfirmDialogResult;
  }
  return { confirmed: false };
}
